import axios from 'axios'
import { toast } from 'react-toastify'
import config from '../config/environment'

const api = axios.create({
  baseURL: config.ADMIN_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = '/admin/login'
    }
    return Promise.reject(error)
  }
)

export const organisationAdminService = {
  async listOrganisations() {
    try {
      const response = await api.get('/organisations')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load organisations'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async getOrganisationById(organisationId) {
    try {
      const response = await api.get(`/organisations/${organisationId}`)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load organisation'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async getOrganisationUsers(organisationId) {
    try {
      const response = await api.get(`/organisations/${organisationId}/users`)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load organisation users'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async listOrganisationWorkflowConfigs(organisationId) {
    try {
      const response = await api.get(`/organisations/${organisationId}/workflow-configs`)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load workflow configs'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async getOrganisationWorkflowConfig(organisationId, workflowConfigId) {
    try {
      const response = await api.get(
        `/organisations/${organisationId}/workflow-configs/${workflowConfigId}`
      )
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load workflow config'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async listOrganisationWorkflows(organisationId, { page = 1, limit = 25, search = '', status } = {}) {
    try {
      const params = { page, limit }
      if (search) params.search = search
      if (status) params.status = status
      const response = await api.get(`/organisations/${organisationId}/workflows`, { params })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load workflows'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async getOrganisationWorkflow(organisationId, workflowId) {
    try {
      const response = await api.get(`/organisations/${organisationId}/workflows/${workflowId}`)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load workflow'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async listOrganisationWorkflowLeads(organisationId, workflowId, { page = 1, limit = 25, search = '' } = {}) {
    try {
      const params = { page, limit }
      if (search) params.search = search
      const response = await api.get(
        `/organisations/${organisationId}/workflows/${workflowId}/leads`,
        { params }
      )
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load workflow leads'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async getOrganisationLeadPersons(organisationId, leadId) {
    try {
      const response = await api.get(`/organisations/${organisationId}/leads/${leadId}/persons`)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load lead persons'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },
}

export default organisationAdminService
